import { Select } from 'antd';
import { sortBy, uniqBy } from 'lodash';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Spinner } from 'components/ui';
import {
  getAssetSite,
  getStrippedUniqueIdLabel,
} from 'horizon/components/Assets/AssetDetails/helpers';
import {
  type AtlasGqlAsset,
  type AtlasGqlFilterBy,
  useGetAssetsForAssetPickerQuery,
} from 'types/atlas-graphql';

import { FormFieldContainer, StyledSelect } from './AssetPicker.style';
import { type AssetHierarchyItem, type AssetTypeOption } from './types';

type AssetPickerItemProps = {
  assetType?: AssetTypeOption;
  defaultValue?: string;
  disabled?: boolean;
  disableSubAssetUntilAssetTypeIsSelected?: boolean;
  filterAssetTypeOption?: (asset: AtlasGqlAsset, parentAsset?: AssetHierarchyItem) => boolean;
  getPopupContainer?: (triggerNode: HTMLElement) => HTMLElement;
  onChange: (asset?: AssetHierarchyItem) => void;
  onChangeAssetType?: (assetTypeId?: string) => void;
  parentId?: string;
  parentHierarchyAsset?: AssetHierarchyItem;
  testId?: string;
  allowClear?: boolean;
};

export const AssetPickerItem: React.FunctionComponent<AssetPickerItemProps> = ({
  assetType,
  defaultValue,
  disabled = false,
  disableSubAssetUntilAssetTypeIsSelected = false,
  filterAssetTypeOption,
  getPopupContainer,
  onChange,
  onChangeAssetType,
  parentId,
  parentHierarchyAsset,
  testId,
  allowClear,
}) => {
  const { t } = useTranslation();
  const [workingAssetType, setWorkingAssetType] = useState<AssetTypeOption>();

  const filterBy: AtlasGqlFilterBy[] = [];
  if (parentId && parentId !== '') {
    filterBy.push({ key: 'childrenOfIds', values: [parentId] });
  }
  if (assetType?.id) {
    filterBy.push({ key: 'assetTypeIds', values: [assetType.id] });
  }

  const { data, loading } = useGetAssetsForAssetPickerQuery({
    variables: {
      filterBy,
    },
    skip: !assetType && !parentId,
  });

  const options: AssetHierarchyItem[] = sortBy(
    data?.assetsWithMetadata?.items
      ?.filter(
        (asset: AtlasGqlAsset) => !workingAssetType || asset?.assetType?.id === workingAssetType.id
      )
      ?.map((asset: AtlasGqlAsset) => ({
        assetId: asset.id,
        assetName: getStrippedUniqueIdLabel(asset, { keepTypeWithoutIdentifier: false }),
        assetTypeId: asset.assetType?.id,
        assetTypeName: asset.assetType?.name,
        siteType: getAssetSite(asset).siteType,
      })) ?? [],
    'assetName'
  );

  const assetTypeOptions = uniqBy(
    data?.assetsWithMetadata?.items
      ?.filter((asset: AtlasGqlAsset) =>
        filterAssetTypeOption ? filterAssetTypeOption(asset, parentHierarchyAsset) : true
      )
      .map((asset: AtlasGqlAsset) => asset.assetType) ?? [],
    id => id
  ).filter(Boolean);

  useEffect(() => {
    if (defaultValue && !assetType) {
      const defaultValueAssetTypeId = options.find(
        ({ assetId }) => assetId === defaultValue
      )?.assetTypeId;

      if (defaultValueAssetTypeId) {
        handleChangeAssetType(defaultValueAssetTypeId, true);
      }
    }
  }, [defaultValue, options]);

  const handleChange = (assetId: string): void => {
    if (!assetId) {
      return onChange(undefined);
    }

    const selectedAsset = options.find(option => option.assetId === assetId);
    if (selectedAsset) {
      onChange(selectedAsset);
      handleChangeAssetType(selectedAsset.assetTypeId);
    }
  };

  const handleChangeAssetType = (typeId?: string, isSettingDefault?: boolean) => {
    const selectedType = assetTypeOptions.find(assetType => assetType?.id === typeId);
    setWorkingAssetType(selectedType ?? undefined);

    /**
     * Parent change handler should not be called when the default (i.e.
     * existing value) is being selected
     */
    if (onChangeAssetType && workingAssetType?.id !== selectedType?.id && !isSettingDefault) {
      onChangeAssetType(typeId);
    }
  };

  if (loading) {
    return <Spinner />;
  }

  if (!options?.length) {
    return null;
  }

  return (
    <FormFieldContainer>
      {!assetType && (
        <StyledSelect
          data-testid={`asset-picker-asset-type${testId ? `-${testId}` : ''}`}
          placeholder={t('settings.selectAssetType', 'Select Asset Type')}
          onChange={(typeId: string) => handleChangeAssetType(typeId)}
          value={workingAssetType?.id}
          disabled={disabled}
          virtual={false}
          allowClear={allowClear}
          getPopupContainer={getPopupContainer}
        >
          {assetTypeOptions.map(assetType => (
            <Select.Option key={assetType?.id} value={assetType?.id}>
              {assetType?.name}
            </Select.Option>
          ))}
        </StyledSelect>
      )}
      <StyledSelect
        data-testid={`asset-picker-asset${testId ? `-${testId}` : ''}`}
        showSearch
        placeholder={t('assets.selectNamed', 'Select {{name}}', {
          name: assetType?.name ?? workingAssetType?.name ?? t('assets.subAsset', 'sub-asset'),
        })}
        optionFilterProp="name"
        onChange={handleChange}
        filterOption={(input, option) =>
          option?.name.toLowerCase().indexOf(input.toLowerCase()) >= 0
        }
        value={defaultValue}
        disabled={
          disabled ||
          (disableSubAssetUntilAssetTypeIsSelected && !workingAssetType?.id && !assetType)
        }
        virtual={false}
        allowClear={allowClear}
        getPopupContainer={getPopupContainer}
      >
        {options.map(({ assetId, assetName }) => (
          <Select.Option key={assetId} value={assetId} name={assetName}>
            {assetName}
          </Select.Option>
        ))}
      </StyledSelect>
    </FormFieldContainer>
  );
};
